/**
 * logger.ts
 *
 * Centralized structured logger for ScholarMinds.
 *
 * - In development: writes to browser console with level prefix.
 * - In production: silently sends to Sentry (breadcrumbs + exception capture)
 *   and Azure Application Insights (traces + exceptions).
 * - Console output is stripped from production builds by Vite (see vite.config.ts).
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.info('Student loaded', { studentId });
 *   logger.error('Failed to save fee', error, { feeId });
 */
import { addBreadcrumb, captureException as sentryCaptureException, isSentryEnabled } from './sentry';
import { trackAppTrace, trackAppException, SeverityLevel } from './appInsights';

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDev = import.meta.env.DEV;

function toSentrySeverity(level: LogLevel): 'debug' | 'info' | 'warning' | 'error' {
  return level === 'warn' ? 'warning' : level;
}

function toAppInsightsSeverity(level: LogLevel): SeverityLevel {
  switch (level) {
    case 'debug': return SeverityLevel.Verbose;
    case 'info':  return SeverityLevel.Information;
    case 'warn':  return SeverityLevel.Warning;
    case 'error': return SeverityLevel.Error;
  }
}

function stringifyContext(context: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(context).map(([k, v]) => [k, v === null || v === undefined ? '' : String(v)])
  );
}

function log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
  // Console output in dev (stripped in production builds) — kept synchronous
  // since it's just a local console write, not a network-bound vendor call.
  if (isDev) {
    const fn =
      level === 'error' ? console.error :
      level === 'warn'  ? console.warn  :
      level === 'debug' ? console.debug :
                          console.info;
    fn(`[${level.toUpperCase()}] ${message}`, context ?? '');
  }

  // Vendor telemetry fan-out is deferred off the caller's critical path (e.g. an
  // axios interceptor awaiting this before resolving/rejecting to the app code).
  queueMicrotask(() => {
    // Sentry breadcrumb (builds a trail of events for each error report)
    if (isSentryEnabled()) {
      addBreadcrumb(message, `app.${level}`, toSentrySeverity(level), context as Record<string, any>);
    }

    // App Insights trace (searchable in Azure portal → Logs → traces table)
    trackAppTrace(
      message,
      toAppInsightsSeverity(level),
      context ? stringifyContext(context) : undefined
    );
  });
}

/**
 * Centralized application logger.
 *
 * @example
 * logger.info('Fee submitted', { feeId: '123', amount: 5000 });
 * logger.warn('Token near expiry', { expiresIn: '45s' });
 * logger.error('Failed to load students', error, { classId: '5A' });
 */
export const logger = {
  debug: (message: string, context?: Record<string, unknown>): void =>
    log('debug', message, context),

  info: (message: string, context?: Record<string, unknown>): void =>
    log('info', message, context),

  warn: (message: string, context?: Record<string, unknown>): void =>
    log('warn', message, context),

  /**
   * Log an error message.
   * If an Error object is provided, it is captured in Sentry (with stack trace)
   * and App Insights (exceptions table).
   *
   * @param message  - Human-readable description of what failed
   * @param error    - The Error object (optional but strongly recommended)
   * @param context  - Additional key/value pairs for debugging
   */
  error: (message: string, error?: Error | unknown, context?: Record<string, unknown>): void => {
    log('error', message, {
      ...context,
      ...(error instanceof Error ? { errorMessage: error.message } : {}),
    });

    if (error instanceof Error) {
      // Full exception with stack trace to Sentry
      sentryCaptureException(error, context as Record<string, any>);

      // Exception to App Insights failures blade
      trackAppException(
        error,
        context ? stringifyContext(context) : undefined
      );
    }
  },
};
